import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", city: "", state: "", zip: "", county: "", phone: "", notes: "", affidavit_form_id: null };

export function CourtDialog({ court, onClose, onSaved }) {
  const isEdit = Boolean(court?.id);
  const [f, setF] = useState({ ...EMPTY, ...(court || {}) });
  const [apply, setApply] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);
  useEffect(() => { api.get("/court-forms").then((r) => setForms(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Court name is required");
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, k === "affidavit_form_id" ? (f[k] || null) : (f[k] || "")]));
      if (isEdit) {
        const { data } = await api.patch(`/courts/${court.id}`, { ...body, apply_to_open_jobs: apply });
        toast.success(`Court updated${data.jobs_updated ? ` · ${data.jobs_updated} open job${data.jobs_updated > 1 ? "s" : ""} corrected` : ""}`);
      } else { await api.post("/courts", body); toast.success("Court added"); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save court"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="court-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Court" : "New Court"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label className="sm-label">Court name *</Label><Input data-testid="court-name" value={f.name} onChange={set("name")} placeholder="e.g. Superior Court — Sussex County" /></div>
          <div><Label className="sm-label">Street address</Label><Input data-testid="court-address" value={f.address} onChange={set("address")} /></div>
          <div className="grid grid-cols-3 gap-2">
            <Input data-testid="court-city" placeholder="City" value={f.city} onChange={set("city")} />
            <Input data-testid="court-state" placeholder="State" value={f.state} onChange={set("state")} />
            <Input data-testid="court-zip" placeholder="Zip" value={f.zip} onChange={set("zip")} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="court-county" placeholder="County" value={f.county} onChange={set("county")} />
            <Input data-testid="court-phone" placeholder="Phone" value={f.phone} onChange={set("phone")} />
          </div>
          <Textarea data-testid="court-notes" rows={2} placeholder="Notes (filing hours, clerk contact, parking…)" value={f.notes} onChange={set("notes")} />
          <div>
            <Label className="sm-label">Official affidavit form (from Court Forms library)</Label>
            <select data-testid="court-form-select" className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={f.affidavit_form_id || ""} onChange={(e) => setF((p) => ({ ...p, affidavit_form_id: e.target.value || null }))}>
              <option value="">None — use the Simply Served affidavit</option>
              {forms.map((cf) => <option key={cf.id} value={cf.id}>{cf.form_number ? `${cf.form_number} · ` : ""}{cf.name}{cf.file ? "" : " (no PDF uploaded)"}</option>)}
            </select>
            <div className="text-xs text-slate-500 mt-1">When set, affidavits for jobs from this court use the official PDF (fields auto-filled where the PDF is fillable), with the detailed Simply Served affidavit attached behind it.</div>
          </div>
          {isEdit && (
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-[#2d7bd0]" checked={apply} onChange={(e) => setApply(e.target.checked)} data-testid="court-apply-open-jobs" />
              <span>Also correct the court name/address on all <b>open</b> jobs using this court{court.jobs ? ` (${court.jobs} job${court.jobs > 1 ? "s" : ""} total)` : ""}</span>
            </label>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="court-cancel">Cancel</Button>
            <Button type="submit" className="sm-green" disabled={saving} data-testid="court-save">{saving ? "Saving…" : isEdit ? "Save changes" : "Add court"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
